/**
 * ExperimentsPage — holder-level experiment list + creation.
 *
 * Evidence class is derived server-side from the stage and surfaced
 * prominently: ad_creative comparisons are observational (Google's
 * rotation biases delivery — directional only), internal stages are
 * randomized. Ad-creative tests are built from already-deployed
 * creatives (their channel ad refs become the variant bindings).
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { AdoptCreateExperimentRequest, AdoptExperimentDto } from '@/lib/api';
import { ARCHE_PATH } from '../constants';
import {
  useAdoptCampaigns,
  useAdoptCreatives,
  useAdoptExperiments,
  useAdoptGoals,
  useCreateAdoptExperiment,
} from '../hooks/useAdvertisingOptimization';

interface Props {
  holderId: string;
}

export function EvidenceClassBadge({ evidenceClass }: { evidenceClass: string }) {
  const observational = evidenceClass === 'observational';
  return (
    <span
      title={
        observational
          ? "Channel-rotated comparison: the ad platform's delivery optimization biases which users see which ad, so differences are directional, not causal."
          : 'Randomized assignment: differences between variants are causal evidence.'
      }
      className={`text-xs rounded px-1.5 py-0.5 ${
        observational ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'
      }`}
    >
      {observational ? 'observational' : 'randomized'}
    </span>
  );
}

const STATUS_STYLES: Record<string, string> = {
  draft: 'bg-muted text-muted-foreground',
  running: 'bg-emerald-100 text-emerald-800',
  paused: 'bg-amber-100 text-amber-800',
  completed: 'bg-sky-100 text-sky-800',
  archived: 'bg-muted text-muted-foreground',
};

const INTERNAL_STAGES = ['landing', 'in_product', 'onboarding_path'] as const;

function slugifyKey(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);
}

function CreateExperimentForm({ holderId, onDone }: Props & { onDone: () => void }) {
  const createExperiment = useCreateAdoptExperiment(holderId);
  const goalsQuery = useAdoptGoals(holderId);
  const campaignsQuery = useAdoptCampaigns(holderId);

  const [name, setName] = useState('');
  const [hypothesis, setHypothesis] = useState('');
  const [stage, setStage] = useState<AdoptExperimentDto['stage']>('ad_creative');
  const [trafficPct, setTrafficPct] = useState('100');
  const [primaryGoalId, setPrimaryGoalId] = useState('');
  const [campaignId, setCampaignId] = useState('');
  const [selectedCreativeIds, setSelectedCreativeIds] = useState<string[]>([]);
  const [internalVariants, setInternalVariants] = useState([
    { key: 'control', name: 'Control', isControl: true },
    { key: 'variant-b', name: 'Variant B', isControl: false },
  ]);

  const isAdCreative = stage === 'ad_creative';
  const creativesQuery = useAdoptCreatives(
    holderId,
    isAdCreative ? campaignId || undefined : undefined,
  );
  const deployedCreatives = useMemo(
    () => (creativesQuery.data ?? []).filter((c) => c.externalAdRef),
    [creativesQuery.data],
  );

  const toggleCreative = (id: string): void => {
    setSelectedCreativeIds((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id],
    );
  };

  const canSubmit =
    name.trim().length > 0 &&
    (isAdCreative
      ? selectedCreativeIds.length >= 2
      : internalVariants.length >= 2 && internalVariants.every((v) => v.key && v.name));

  const submit = async (): Promise<void> => {
    const body: AdoptCreateExperimentRequest = {
      key: slugifyKey(name),
      name: name.trim(),
      hypothesis: hypothesis.trim() || undefined,
      stage,
      unitType: 'visitor',
      trafficPct: Number(trafficPct) || 100,
      primaryGoalId: primaryGoalId || undefined,
      variants: isAdCreative
        ? selectedCreativeIds.map((id, i) => {
            const creative = deployedCreatives.find((c) => c.id === id)!;
            return {
              variantKey: slugifyKey(`${creative.angle}-${id.slice(-6)}`),
              name: `${creative.angle.replace('_', ' ')} (${id.slice(-6)})`,
              isControl: i === 0,
              externalRef: {
                channel: 'google_ads' as const,
                externalAdRef: creative.externalAdRef!,
                creativeId: creative.id,
              },
            };
          })
        : internalVariants.map((v) => ({
            variantKey: slugifyKey(v.key),
            name: v.name,
            isControl: v.isControl,
          })),
    };
    await createExperiment.mutateAsync(body);
    onDone();
  };

  return (
    <div className="rounded-lg border border-border p-4 flex flex-col gap-3">
      <h2 className="font-medium">New experiment</h2>
      <input
        className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
        placeholder="Experiment name"
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <textarea
        rows={2}
        className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
        placeholder="Hypothesis (optional)"
        value={hypothesis}
        onChange={(e) => setHypothesis(e.target.value)}
      />
      <div className="flex gap-3 flex-wrap">
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-muted-foreground">Stage</span>
          <select
            className="rounded-md border border-border bg-background px-2 py-1.5"
            value={stage}
            onChange={(e) => setStage(e.target.value as AdoptExperimentDto['stage'])}
          >
            <option value="ad_creative">Ad creative (channel-rotated)</option>
            {INTERNAL_STAGES.map((s) => (
              <option key={s} value={s}>
                {s.replace('_', ' ')} (randomized)
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-sm w-28">
          <span className="text-muted-foreground">Traffic %</span>
          <input
            type="number"
            min={1}
            max={100}
            className="rounded-md border border-border bg-background px-2 py-1.5"
            value={trafficPct}
            onChange={(e) => setTrafficPct(e.target.value)}
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-muted-foreground">
            Primary goal{isAdCreative ? ' (optional)' : ''}
          </span>
          <select
            className="rounded-md border border-border bg-background px-2 py-1.5"
            value={primaryGoalId}
            onChange={(e) => setPrimaryGoalId(e.target.value)}
          >
            <option value="">None yet</option>
            {(goalsQuery.data ?? []).map((g) => (
              <option key={g.goalId} value={g.goalId}>
                {g.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      {isAdCreative ? (
        <div className="flex flex-col gap-2">
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-muted-foreground">Campaign (pick ≥2 deployed creatives)</span>
            <select
              className="rounded-md border border-border bg-background px-2 py-1.5"
              value={campaignId}
              onChange={(e) => {
                setCampaignId(e.target.value);
                setSelectedCreativeIds([]);
              }}
            >
              <option value="">Select campaign…</option>
              {(campaignsQuery.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
          {campaignId && deployedCreatives.length < 2 && !creativesQuery.isLoading && (
            <p className="text-sm text-muted-foreground">
              This campaign has fewer than two deployed creatives — deploy more from the Creative
              Studio first.
            </p>
          )}
          {deployedCreatives.map((c) => (
            <label key={c.id} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={selectedCreativeIds.includes(c.id)}
                onChange={() => toggleCreative(c.id)}
              />
              <span className="capitalize">{c.angle.replace('_', ' ')}</span>
              <span className="text-muted-foreground truncate max-w-md">
                {c.content.headlines
                  .slice(0, 3)
                  .map((h) => h.text)
                  .join(' · ')}
              </span>
            </label>
          ))}
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <span className="text-sm text-muted-foreground">Variants</span>
          {internalVariants.map((v, i) => (
            <div key={i} className="flex items-center gap-2">
              <input
                className="w-40 rounded-md border border-border bg-background px-2 py-1 text-sm"
                value={v.key}
                onChange={(e) =>
                  setInternalVariants(
                    internalVariants.map((x, j) => (j === i ? { ...x, key: e.target.value } : x)),
                  )
                }
                placeholder="variant-key"
              />
              <input
                className="flex-1 rounded-md border border-border bg-background px-2 py-1 text-sm"
                value={v.name}
                onChange={(e) =>
                  setInternalVariants(
                    internalVariants.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)),
                  )
                }
                placeholder="Variant name"
              />
              <label className="flex items-center gap-1 text-xs text-muted-foreground">
                <input
                  type="radio"
                  name="control-variant"
                  checked={v.isControl}
                  onChange={() =>
                    setInternalVariants(
                      internalVariants.map((x, j) => ({ ...x, isControl: j === i })),
                    )
                  }
                />
                control
              </label>
              {internalVariants.length > 2 && (
                <button
                  onClick={() => setInternalVariants(internalVariants.filter((_, j) => j !== i))}
                  className="text-sm text-rose-600"
                  aria-label={`Remove variant ${i + 1}`}
                >
                  ✕
                </button>
              )}
            </div>
          ))}
          <button
            onClick={() =>
              setInternalVariants([
                ...internalVariants,
                { key: `variant-${internalVariants.length + 1}`, name: '', isControl: false },
              ])
            }
            className="self-start text-sm text-primary hover:underline"
          >
            + Add variant
          </button>
        </div>
      )}

      {createExperiment.isError && (
        <div className="text-sm text-rose-600">
          {createExperiment.error instanceof Error
            ? createExperiment.error.message
            : 'Failed to create experiment'}
        </div>
      )}
      <div className="flex gap-2">
        <button
          onClick={() => void submit()}
          disabled={!canSubmit || createExperiment.isPending}
          className="px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-sm disabled:opacity-50"
        >
          Create experiment
        </button>
        <button onClick={onDone} className="px-3 py-1.5 rounded-md border border-border text-sm">
          Cancel
        </button>
      </div>
    </div>
  );
}

export function ExperimentsPage({ holderId }: Props) {
  const experimentsQuery = useAdoptExperiments(holderId);
  const [creating, setCreating] = useState(false);

  return (
    <div className="p-6 max-w-4xl mx-auto flex flex-col gap-4">
      <header className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Experiments</h1>
        {!creating && (
          <button
            onClick={() => setCreating(true)}
            className="px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-sm"
          >
            New experiment
          </button>
        )}
      </header>

      {creating && <CreateExperimentForm holderId={holderId} onDone={() => setCreating(false)} />}

      {experimentsQuery.isLoading ? (
        <p className="text-muted-foreground">Loading experiments…</p>
      ) : (experimentsQuery.data ?? []).length === 0 ? (
        <p className="text-muted-foreground">
          No experiments yet. Test ad creatives against each other, or run randomized landing /
          in-product tests.
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {(experimentsQuery.data ?? []).map((x) => (
            <li key={x.experimentId}>
              <Link
                to={`${ARCHE_PATH}/experiments/${x.experimentId}`}
                className="flex items-center justify-between rounded-lg border border-border p-4 hover:bg-muted/40 transition-colors"
              >
                <div className="flex flex-col gap-1">
                  <span className="font-medium">{x.name}</span>
                  <span className="text-sm text-muted-foreground capitalize">
                    {x.stage.replace('_', ' ')} · {x.trafficPct}% traffic
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <EvidenceClassBadge evidenceClass={x.evidenceClass} />
                  <span
                    className={`text-xs rounded px-1.5 py-0.5 ${STATUS_STYLES[x.status] ?? 'bg-muted'}`}
                  >
                    {x.status}
                  </span>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
